import { StrictMode, Suspense, lazy } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter as Router, Route, Navigate, Routes } from "react-router-dom";
import { Security } from "@okta/okta-react";
import { OktaAuth } from "@okta/okta-auth-js";
import { initializeIcons } from "@fluentui/react";
import { OktaLogin } from "./forms/oktaLogin";
import "./index.css";
import Layout from "./pages/layout/Layout";
import { OktaLogout } from "./forms/oktaLogout";
import { GlobalStyle, gel3Themes } from "@snsw-gel/react";
import { OktaAuthClient } from "./auth/okta";
import { CustomLoginCallback } from "./forms/CustomLoginCallback";
import NoPage from "./pages/NoPage";
import { RequireAuth } from "./components/RequireAuth";
import { RequireProduct } from "./products/RequireProduct";
import { PRODUCT_BONDS, PRODUCT_ATHENA } from "./products/access";
import { isAthenaEnabled } from "./products/athena/config";

// The Athena product surfaces are lazy so the SDK + fluent v9 + chat UI stay
// out of Bonds' initial bundle. Loaded once per session, cached by the browser.
const AthenaSearchPage = lazy(() => import("./products/athena/AthenaSearchPage"));
const AthenaChatPage = lazy(() => import("./products/athena/chat/AthenaChatPage"));

const BASE_PATH = import.meta.env.VITE_BASE_PATH ?? "";

const restoreOriginalUri = async (_oktaAuth: OktaAuth, originalUri: string) =>
  window.location.replace(originalUri || window.location.origin + BASE_PATH + "/home");

initializeIcons();

const container = document.getElementById("root") as HTMLElement;
const root = createRoot(container);

root.render(
  <StrictMode>
    <GlobalStyle themes={gel3Themes}>
      <Router basename={BASE_PATH}>
        <Security oktaAuth={OktaAuthClient} restoreOriginalUri={restoreOriginalUri}>
          <Routes>
            <Route path="/" element={<OktaLogin />} />
            <Route path="/onboarding" element={<Navigate to="/home" replace />} />
            <Route path="/authorization-code/callback" element={<CustomLoginCallback />} />
            <Route path="/logout" element={<OktaLogout />} />
            {/*
              DEV-only visual harness: renders the Athena surface with no Okta
              gate, for headless styling passes against a SAGE_AUTH_MODE=disabled
              proxy. Guarded by import.meta.env.DEV — never exists in a built
              bundle. Not an access-control bypass: the proxy still refuses
              unauthenticated data routes in every deployed environment.
            */}
            {import.meta.env.DEV && isAthenaEnabled() && (
              <Route
                path="/__athena"
                element={
                  <Suspense fallback={null}>
                    <AthenaSearchPage />
                  </Suspense>
                }
              />
            )}
            {import.meta.env.DEV && isAthenaEnabled() && (
              <Route
                path="/__athena-chat"
                element={
                  <Suspense fallback={null}>
                    <AthenaChatPage />
                  </Suspense>
                }
              />
            )}
            {/*
              DEV-only Bonds chat harness: the full Layout (home tiles, chat,
              streaming) with no Okta gate, for headless regression passes
              against a local daphe (which has no auth). Same DEV guard as
              the Athena harnesses — never exists in a built bundle.
            */}
            {import.meta.env.DEV && <Route path="/__bonds" element={<Layout />} />}
            <Route element={<RequireAuth />}>
              <Route
                path="home/*"
                element={
                  <RequireProduct product={PRODUCT_BONDS}>
                    <Layout />
                  </RequireProduct>
                }
              />
              {isAthenaEnabled() && (
                <Route
                  path="athena/search"
                  element={
                    <RequireProduct product={PRODUCT_ATHENA}>
                      <Suspense fallback={null}>
                        <AthenaSearchPage />
                      </Suspense>
                    </RequireProduct>
                  }
                />
              )}
              {isAthenaEnabled() && (
                <Route
                  path="athena/chat"
                  element={
                    <RequireProduct product={PRODUCT_ATHENA}>
                      <Suspense fallback={null}>
                        <AthenaChatPage />
                      </Suspense>
                    </RequireProduct>
                  }
                />
              )}
            </Route>
            <Route path="*" element={<NoPage />} />
          </Routes>
        </Security>
      </Router>
    </GlobalStyle>
  </StrictMode>
);
